import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from '../utils/axios';
import toast from 'react-hot-toast';
import env from '../utils/env';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [token, setToken] = useState(localStorage.getItem('auth_token'));

  // Import env configuration
  const API_URL = env.REACT_APP_API_URL || 'http://localhost:8000/api';

  // Base axios instance is pre-configured in utils/axios

  // Add retry logic for API requests
  axios.interceptors.response.use(undefined, async (error) => {
    if (error.response && error.response.status === 502) {
      // Retry the request once after a short delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      return axios.request(error.config);
    }
    return Promise.reject(error);
  });

  // Add token to requests if available
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const storedToken = localStorage.getItem('auth_token');
      if (!storedToken) {
        setLoading(false);
        return;
      }

      // Set token state
      setToken(storedToken);

      // Check if token is valid by fetching user data
      const response = await axios.get('/auth/user');
      if (response.data.success) {
        setUser(response.data.user);
      } else {
        // Token is invalid, remove it
        localStorage.removeItem('auth_token');
        setToken(null);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      // Token is invalid, remove it
      localStorage.removeItem('auth_token');
      setToken(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (credentials) => {
    try {
      setIsLoading(true);
      const response = await axios.post('/auth/login', credentials);

      if (response.data.success) {
        const { user, token } = response.data;

        // Store token and set auth header
        localStorage.setItem('auth_token', token);
        setToken(token);

        // Set user
        setUser(user);

        toast.success('Login successful!');
        return { success: true };
      } else {
        toast.error(response.data.message || 'Login failed');
        return { success: false, message: response.data.message };
      }
    } catch (error) {
      console.error('Login error:', error);
      const message = error.response?.data?.message || 'Login failed. Please try again.';
      toast.error(message);
      return { success: false, message };
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (userData) => {
    try {
      setIsLoading(true);
      console.log('Starting registration with data:', userData);
      console.log('API URL:', API_URL);

      const response = await axios.post('/auth/register', userData);

      console.log('Registration response:', response);

      if (response.data.success) {
        const { user, token } = response.data;

        // Store token and set auth header
        localStorage.setItem('auth_token', token);
        setToken(token);

        // Set user
        setUser(user);

        toast.success('Registration successful!');
        return { success: true };
      } else {
        console.log('Registration failed with response:', response.data);
        toast.error(response.data.message || 'Registration failed');
        return { success: false, message: response.data.message, errors: response.data.errors };
      }
    } catch (error) {
      console.error('Registration error:', error);
      console.log('Error response:', error.response);
      const message = error.response?.data?.message || 'Registration failed. Please try again.';
      const errors = error.response?.data?.errors || {};
      toast.error(message);
      return { success: false, message, errors };
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      // Call logout endpoint
      await axios.post('/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear local data regardless of API call success
      localStorage.removeItem('auth_token');
      setToken(null);
      setUser(null);
      toast.success('Logged out successfully');
    }
  };

  const loginWithGoogle = async () => {
    // Prevent multiple OAuth attempts
    if (isLoading) {
      console.log('OAuth already in progress, ignoring request');
      return;
    }

    try {
      setIsLoading(true);
      console.log('Starting Google OAuth flow');

      // Get Google OAuth URL from backend
      const response = await axios.get('/auth/google');
      console.log('Got OAuth URL response:', response.data);

      if (!response.data.success || !response.data.redirect_url) {
        throw new Error(response.data.message || 'Failed to get Google OAuth URL');
      }

      const googleUrl = response.data.redirect_url;
      console.log('Opening popup with URL:', googleUrl);

      // Open popup window for Google OAuth
      const popup = window.open(
        googleUrl,
        'google-oauth',
        'width=500,height=600,scrollbars=yes'
      );

      if (!popup) {
        throw new Error('Failed to open popup window. Please allow popups for this site.');
      }

      // Listen for messages from the popup
      return new Promise((resolve, reject) => {
        const messageHandler = (event) => {
          // Accept messages from both frontend and backend origins
          const allowedOrigins = [
            window.location.origin,
            env.REACT_APP_FRONTEND_URL ? new URL(env.REACT_APP_FRONTEND_URL).origin : 'http://localhost',
            'http://localhost:8000', // Backend API origin for local development
            'https://llmchecks.com'
          ];

          if (!allowedOrigins.includes(event.origin)) {
            console.log('Ignoring message from unexpected origin:', event.origin);
            return;
          }

          if (event.data.type === 'google-oauth-callback') {
            window.removeEventListener('message', messageHandler);

            const data = event.data.data;
            if (data.success) {
              if (data.token) {
                // Existing user
                localStorage.setItem('auth_token', data.token);
                setToken(data.token);
                setUser(data.user);
                toast.success('Login successful!');
                resolve({ success: true });
              } else {
                // New user, needs to complete registration
                localStorage.setItem('google_user_temp', JSON.stringify(data.googleUser));
                resolve({
                  success: true,
                  requiresOrgSetup: true,
                  googleUser: data.googleUser
                });
              }
            } else {
              reject(new Error(data.message || 'Google authentication failed'));
            }
          }
        };

        window.addEventListener('message', messageHandler);

        // Add timeout to prevent hanging
        setTimeout(() => {
          window.removeEventListener('message', messageHandler);
          reject(new Error('Google authentication timed out'));
        }, 120000); // 2 minutes timeout
      });
    } catch (error) {
      console.error('Google sign-in error:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const completeGoogleRegistration = async (organizationData) => {
    try {
      setIsLoading(true);

      // Get Google user data from temporary storage
      const googleUserData = localStorage.getItem('google_user_temp');
      if (!googleUserData) {
        throw new Error('Google user data not found. Please try signing in again.');
      }

      const googleUser = JSON.parse(googleUserData);

      const registrationData = {
        ...organizationData,
        google_id: googleUser.google_id,
        name: googleUser.name,
        email: googleUser.email,
      };

      console.log('Completing Google registration with data:', registrationData);

      const response = await axios.post('/auth/google/complete', registrationData);

      console.log('Google registration completion response:', response);

      if (response.data.success) {
        const { user, token } = response.data;

        // Store token and set auth header
        localStorage.setItem('auth_token', token);
        setToken(token);

        // Set user
        setUser(user);

        // Clear temporary Google user data
        localStorage.removeItem('google_user_temp');

        toast.success('Registration completed successfully!');
        return { success: true };
      } else {
        console.log('Google registration completion failed with response:', response.data);
        toast.error(response.data.message || 'Registration completion failed');
        return { success: false, message: response.data.message, errors: response.data.errors };
      }
    } catch (error) {
      console.error('Google registration completion error:', error);
      console.log('Error response:', error.response);
      const message = error.response?.data?.message || 'Registration completion failed. Please try again.';
      const errors = error.response?.data?.errors || {};
      toast.error(message);
      return { success: false, message, errors };
    } finally {
      setIsLoading(false);
    }
  };

  const value = {
    user,
    loading,
    isLoading,
    login,
    logout,
    register,
    loginWithGoogle,
    completeGoogleRegistration,
    checkAuth,
    token,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
